/**
 * Justificante de participación (vista web).
 *
 * - Header oliva "Pago confirmado" (o ámbar "Pendiente de pago").
 * - Tarjeta de participación con referencia mono, pagador, DNI enmascarado,
 *   cantidad, total.
 * - Papeletas nominativas numeradas (`N.º 0105`) con descarga QR.
 * - Predicción de porra cuando aplica.
 * - CTA "Descargar PDF justificante".
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';


interface ShareView {
  group_buy: {
    slug: string;
    type: string;
    title: string;
    organizer_name: string;
    status: string;
    is_freelance_invoice?: boolean;
    is_wallet_pena?: boolean;
    is_tip_jar?: boolean;
  };
  share: {
    order_reference: string;
    buyer_name: string;
    buyer_dni?: string;
    quantity: number;
    unit_price: number;
    total_amount: number;
    status: string;
    created_at: string;
  };
  tickets_assigned: Array<{
    ticket_number: number;
    status: string;
    qr_token?: string;
    amount_paid: number;
  }>;
  porra_prediction?: {
    home_score_pred: number;
    away_score_pred: number;
    score_points?: number;
    is_winner: boolean;
  } | null;
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function maskDni(dni: string): string {
  if (dni.length < 5) return '***';
  return dni.slice(0, 2) + '••••' + dni.slice(-2);
}


export default function GroupBuySharePage() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>();
  const [data, setData] = useState<ShareView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !orderId) return;
    api.get(`/public/group-buys/${slug}/share/${orderId}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Justificante no encontrado'))
      .finally(() => setLoading(false));
  }, [slug, orderId]);

  if (loading)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center text-ink-500 font-sans">
        Un momento…
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center font-sans">
        <div className="max-w-sm">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Error
          </div>
          <h1 className="font-display font-bold text-[28px] text-ink mb-2 tracking-[-0.02em]">
            Justificante no encontrado
          </h1>
          <p className="text-ink-700 text-[14.5px]">¿El enlace está completo?</p>
        </div>
      </div>
    );

  const { group_buy: gb, share, tickets_assigned, porra_prediction } = data;
  const isPaid = share.status === 'paid';
  const pdfUrl = `/api/public/group-buys/${slug}/share/${orderId}/pdf`;

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      {/* Header oliva (pagado) o ámbar (pendiente) */}
      <header
        className={
          isPaid ? 'bg-olive text-paper' : 'bg-amberCC text-paper'
        }
      >
        <div className="max-w-2xl mx-auto px-6 py-10 md:py-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-paper/80 mb-3">
            · {isPaid ? '✓ Pago confirmado' : '⏳ Pendiente de pago'}
          </div>
          <h1 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.04] tracking-[-0.02em] mb-2">
            {gb.title}
          </h1>
          <p className="text-[14.5px] text-paper/85">
            Organiza: <strong className="text-paper">{gb.organizer_name}</strong>
          </p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-5">
        {/* Datos del share */}
        <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
            · Tu participación
          </div>
          <dl className="space-y-2.5 text-[14px]">
            <Row label="Referencia">
              <span className="font-mono font-semibold text-ink">{share.order_reference}</span>
            </Row>
            <Row label="Pagador">
              <span className="font-display font-semibold text-ink tracking-[-0.01em]">
                {share.buyer_name}
              </span>
            </Row>
            {share.buyer_dni && (
              <Row label="DNI">
                <span className="font-mono text-ink-700" title="DNI enmascarado por privacidad">
                  {maskDni(share.buyer_dni)}
                </span>
              </Row>
            )}
            <Row label="Cantidad">
              <span className="text-ink-700">
                {share.quantity} × {formatEUR(share.unit_price)}
              </span>
            </Row>
            <div className="border-t border-line-soft pt-3 mt-3 flex items-baseline justify-between">
              <dt className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
                Total
              </dt>
              <dd className="font-display font-bold text-[24px] text-olive-700 tracking-[-0.01em] leading-none">
                {formatEUR(share.total_amount)}
              </dd>
            </div>
          </dl>
        </section>

        {/* Papeletas asignadas */}
        {tickets_assigned.length > 0 && (
          <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand mb-4">
              · Tus papeletas nominativas
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {tickets_assigned.map((t) => (
                <div
                  key={t.ticket_number}
                  className="border border-brand-50 bg-brand-50 rounded-xl p-4 text-center"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand mb-1">
                    N.º
                  </div>
                  <div className="font-display font-bold text-[28px] text-brand-700 leading-none mb-3 tracking-[-0.01em]">
                    {String(t.ticket_number).padStart(4, '0')}
                  </div>
                  <a
                    href={`/api/public/group-buys/${slug}/share/${orderId}/ticket/${t.ticket_number}/qr`}
                    className="inline-block px-3 py-1.5 rounded-pill border-[1.5px] border-brand bg-paper text-brand text-[12px] font-semibold hover:bg-brand hover:text-paper transition"
                  >
                    Descargar QR
                  </a>
                </div>
              ))}
            </div>
            <p className="text-[12.5px] text-ink-500 mt-4">
              Cada papeleta está a tu nombre con DNI vinculante (Decreto 325/1988
              de la Junta de Andalucía). Si gana una de tus papeletas, el premio es tuyo.
            </p>
          </section>
        )}

        {/* Predicción porra */}
        {porra_prediction && (
          <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-olive-700 mb-4">
              · Tu predicción
            </div>
            <div className="text-center">
              <div className="font-display font-bold text-[48px] md:text-[64px] text-olive-700 leading-none tracking-[-0.03em] mb-3">
                {porra_prediction.home_score_pred} − {porra_prediction.away_score_pred}
              </div>
              {porra_prediction.score_points !== null &&
                porra_prediction.score_points !== undefined && (
                  <span
                    className={
                      porra_prediction.is_winner
                        ? 'inline-flex items-center gap-2 px-4 py-2 rounded-pill text-[14px] font-bold bg-amberCC-50 text-amberCC-700'
                        : 'inline-flex items-center gap-2 px-4 py-2 rounded-pill text-[14px] font-bold bg-line-soft text-ink-700'
                    }
                  >
                    {porra_prediction.is_winner && (
                      <span aria-hidden>🏆</span>
                    )}
                    {porra_prediction.score_points} pts
                    {porra_prediction.is_winner && ' · Ganador'}
                  </span>
                )}
            </div>
          </section>
        )}

        {/* Descarga PDF · variante factura si es Oficina Facil */}
        {isPaid && (
          <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
              · {gb.is_freelance_invoice ? 'Factura' : 'Justificante en PDF'}
            </div>
            {gb.is_freelance_invoice ? (
              <>
                <a
                  href={`/api/public/group-buys/${slug}/factura/${orderId}.pdf`}
                  className="block w-full px-5 py-3.5 text-center rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition"
                >
                  Descargar factura · PDF
                </a>
                <p className="text-[12.5px] text-ink-500 mt-3">
                  Factura {data.group_buy.is_freelance_invoice ? 'simplificada' : ''}{' '}
                  emitida según RD 1619/2012 · número correlativo, NIF del
                  emisor, concepto, base, IVA y total.
                </p>
              </>
            ) : (
              <>
                <a
                  href={pdfUrl}
                  className="block w-full px-5 py-3.5 text-center rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition"
                >
                  Descargar PDF justificante
                </a>
                <p className="text-[12.5px] text-ink-500 mt-3">
                  El PDF lleva tu nombre, DNI, QR de verificación y el sello legal aplicable.
                </p>
              </>
            )}
          </section>
        )}

        <div className="text-center pt-4">
          <a
            href={`/g/${slug}`}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink"
          >
            ← Volver al pagafácil
          </a>
        </div>
      </main>
    </div>
  );
}


function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-500 text-[13px]">{label}</dt>
      <dd className="text-[14px]">{children}</dd>
    </div>
  );
}
